<style scoped>
    .container {
        background: white;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        max-height: 100vh;
        padding: 10px 0;
        position: sticky;
        top: 0;
        z-index: 1;
    }

    .controls-toggle {
        color: var(--highlight);
        font-weight: bold;
        overflow: hidden;
    }

    .controls {
        overflow-y: auto;
    }

    .section-heading {
        color: var(--muted);
        margin: 20px 0 10px;
    }

    .filter-list {
        margin: 10px 0;
    }

    .filter-toggle {
        font-weight: bold;
        overflow: hidden;
        padding: 10px 0;
    }

    .filter-rule {
        margin: 0;
    }

    .filter-indicator {
        font-size: 1.5em;
        line-height: 0.7;
    }

    div.not-sticky .container.mobile-controls-container{
      position:initial;
    }
    div.not-sticky .cp-icon, div.not-sticky .cp-icon__container{
      position:initial;
    }

    .filter-item {
        padding: 3px 0;
    }
</style>

<template>
    <div class="container mobile-controls-container">
        <div>
            <cp-grid style="--grid-gutter: 10px; margin-bottom: 10px;">
                <cp-grid-item span="auto">
                    <cp-text-input block v-model="currentQuery" aria-label="Search">
                        <template #prefix>
                            <cp-icon name="search" square></cp-icon>
                        </template>
                        <template #suffix>
                            <!--NOTE: The "submit" handler is debounced, so `apply` happens before the request is made.-->
                            <cp-unstyled-button link @click="handleClear">
                                <cp-icon name="times" square title="Clear"></cp-icon>
                            </cp-unstyled-button>
                        </template>
                    </cp-text-input>
                </cp-grid-item>

                <cp-grid-item>
                    <cp-button type="submit" primary block @click="apply">
                        <cp-icon name="arrow-right" square title="Search"></cp-icon>
                    </cp-button>
                </cp-grid-item>
            </cp-grid>

            <div>
                <cp-grid class="controls-toggle" align="center" justify="spaceBetween">
                    <cp-grid-item>
                        <cp-unstyled-button link @click="handleToggleClick">
                            <cp-icon name="filter"></cp-icon><!--TODO-->
                            Sort &amp; Filter

                            <template v-if="allAppliedFilters.length !== 0">
                                ({{ allAppliedFilters.length }})
                            </template>
                        </cp-unstyled-button>
                    </cp-grid-item>
                    <cp-grid-item :span="3">
                        <cp-button block :style="{visibility: open ? null : 'hidden'}" @click="apply">Apply</cp-button>
                    </cp-grid-item>
                </cp-grid>
            </div>
        </div>

        <cp-expander v-if="open" class="controls">
            <div>
                <div class="section-heading">Sort by</div>
                <cp-sort-controls v-model="currentSort"></cp-sort-controls>
            </div>

            <hr />

            <div>
                <cp-grid class="section-heading" justify="spaceBetween">
                    <cp-grid-item>Filter by</cp-grid-item>
                    <cp-grid-item>
                        <cp-unstyled-button link v-if="allCurrentFilters.length !== 0" @click="resetFilters">Reset filters</cp-unstyled-button>
                    </cp-grid-item>
                </cp-grid>

                <cp-details-box
                    v-for="filter in filtersMetadata"
                    :key="filter.key"
                    class="filter-list"
                    :indicator="false"
                    :open="openFilter === filter.key"
                    :style="{opacity: disabledFilters.includes(filter.key) ? 0.5 : null}"
                    @open="openFilter = filter.key"
                >
                    <template #summary="{currentlyOpen}">
                        <cp-grid class="filter-toggle" justify="spaceBetween">
                            <cp-grid-item>
                                {{ filter.label }}

                                <template v-if="currentFilters[filter.key].length !== 0">
                                    ({{ currentFilters[filter.key].length }})
                                </template>
                            </cp-grid-item>

                            <cp-grid-item class="filter-indicator">
                                {{ currentlyOpen ? '&ndash;' : '+' }}
                            </cp-grid-item>
                        </cp-grid>
                    </template>

                    <cp-checkable-item
                        type="checkbox"
                        v-for="option in availableFilters[filter.key]"
                        :key="option.value"
                        class="filter-item"
                        v-model="currentFilters[filter.key]"
                        :value="option.value"
                        :disabled="disabledFilters.includes(filter.key)"
                    >
                        {{ option.label }}
                    </cp-checkable-item>
                </cp-details-box>
            </div>

            <cp-button block @click="apply">Apply</cp-button>
        </cp-expander>
    </div>
</template>

<script>
    import CpButton from '../button';
    import CpCheckableItem from './checkable-item';
    import CpDetailsBox from '../details-box';
    import CpExpander from '../expander';
    import CpSortControls from './sort-controls';
    import CpGrid from '../grid';
    import CpGridItem from '../grid-item';
    import CpIcon from '../icon';
    import CpTextInput from '../inputs/text';
    import CpUnstyledButton from '../unstyled-button';
    import filtersMetadata from './filters-metadata';

    export default {
        components: {
            CpButton,
            CpCheckableItem,
            CpDetailsBox,
            CpExpander,
            CpSortControls,
            CpGrid,
            CpGridItem,
            CpIcon,
            CpTextInput,
            CpUnstyledButton,
        },

        props: [
            'query',
            'sort',
            'filters',
            'availableFilters',
        ],

        data() {
            return {
                filtersMetadata,
                currentQuery: this.query,
                currentSort: this.sort,
                currentFilters: {...this.filters},
                open: false,
                openFilter: null,
            };
        },

        computed: {
            allAppliedFilters() {
                return [].concat(...Object.values(this.filters));
            },

            allCurrentFilters() {
                return [].concat(...Object.values(this.currentFilters));
            },

            disabledFilters() {
                return this.filtersMetadata
                    .filter(filter => filter.isDisabled(this.currentFilters))
                    .map(filter => filter.key);
            },
        },

        methods: {
            handleClear() {
                this.currentQuery = '';
                if (!this.open) {
                    this.apply();
                }
            },

            handleToggleClick(event) {
                this.open = !this.open;
            },

            resetFilters() {
                Object.keys(this.currentFilters).forEach(filter => this.currentFilters[filter] = []);
            },

            apply() {
                this.$emit('change', {
                    query: this.currentQuery,
                    sort: this.currentSort,
                    filters: {...this.currentFilters},
                });
                this.open = false;
                if (document.activeElement) {
                    document.activeElement.blur();
                }
            },
        },

        watch: {
            open(open) {
                if (!open) {
                    this.openFilter = null;
                }
            },

            disabledFilters(disabledFilters) {
                disabledFilters.forEach(filter => this.currentFilters[filter] = []);
            },
        },
    };
</script>
